import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Lock, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";

const formatErr = (d) => {
    if (!d) return "Login failed";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join(" ");
    return d.msg || String(d);
};

const AdminLogin = () => {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email.trim(), password);
            toast.success("Welcome back, Admin!");
            navigate("/admin");
        } catch (err) {
            toast.error(formatErr(err?.response?.data?.detail) || "Invalid credentials");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[80vh] grid md:grid-cols-2">
            <div className="hidden md:flex bg-blue-900 text-white relative overflow-hidden">
                <div className="grain-overlay" />
                <div className="relative z-10 p-12 flex flex-col justify-between w-full">
                    <div className="font-display font-bold text-lg tracking-tight">Shillong Morning Teer</div>
                    <div>
                        <div className="text-yellow-400 font-semibold text-xs uppercase tracking-widest">Admin Portal</div>
                        <h2 className="font-display font-bold text-4xl mt-2 tracking-tight leading-[1.05]">
                            Daily results,<br/>lightning fast.
                        </h2>
                        <p className="mt-4 text-blue-100/80 max-w-md">
                            Sign in to publish today's F/R & S/R across all markets, and manage predictions.
                        </p>
                    </div>
                    <div className="text-xs text-blue-200/60">Secure · JWT · HTTPS</div>
                </div>
            </div>

            <div className="flex items-center justify-center p-6 md:p-12">
                <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="login-form">
                    <div className="text-xs uppercase tracking-widest text-yellow-600 font-semibold">Sign in</div>
                    <h1 className="font-display font-bold text-3xl md:text-4xl text-blue-950 tracking-tight mt-1">
                        Admin Login
                    </h1>
                    <p className="text-slate-500 mt-2 text-sm">
                        Enter admin credentials to manage results.
                    </p>

                    <label className="mt-6 block text-sm font-semibold text-blue-950">Email</label>
                    <div className="mt-2 flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2.5">
                        <Mail className="h-4 w-4 text-slate-500" />
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            data-testid="login-email-input"
                            placeholder="admin@shillongmorning.in"
                            className="w-full bg-transparent outline-none text-sm"
                        />
                    </div>

                    <label className="mt-4 block text-sm font-semibold text-blue-950">Password</label>
                    <div className="mt-2 flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2.5">
                        <Lock className="h-4 w-4 text-slate-500" />
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            data-testid="login-password-input"
                            placeholder="••••••••"
                            className="w-full bg-transparent outline-none text-sm"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="login-submit-btn"
                        className="mt-6 btn-primary w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold disabled:opacity-60"
                    >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        {loading ? "Signing in…" : "Sign in"}
                    </button>

                    <p className="mt-6 text-xs text-slate-400">
                        Only authorized admins can access this panel.
                    </p>
                </form>
            </div>
        </div>
    );
};

export default AdminLogin;
